import React, { useEffect, useState } from "react";
import {
  fetchDashboard,
  totalDevices,
  totalcustomer,
  asset,
  users,
  systeminfo,
} from "../services/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { RxDashboard } from "react-icons/rx";
import { FiUsers } from "react-icons/fi";
import { MdOutlineDevices, MdOutlineInventory2 } from "react-icons/md";
import { BsBuilding } from "react-icons/bs";
import Sparkline from "../components/Sparkline";
import { cumulativeSeries, hasVariation } from "../utils/trend";

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return "-";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const StatCard = ({ label, rows, icon, loading }) => {
  const trend = cumulativeSeries(rows);
  const showTrend = hasVariation(trend);

  return (
    <div className="rounded-lg border border-bgray-200 bg-white p-5 transition-shadow hover:shadow-lg dark:border-darkblack-400 dark:bg-darkblack-600">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-50 text-xl text-success-400">
          {icon}
        </span>
        <span className="text-sm font-semibold text-bgray-600 dark:text-bgray-50">{label}</span>
      </div>

      {loading ? (
        <Skeleton height={36} />
      ) : (
        <>
          {/* A real zero renders as "0" rather than spinning forever, which is
              what the old `value > 0` check did. */}
          <p className="text-3xl font-bold leading-[42px] text-bgray-900 dark:text-white">
            {(rows?.length ?? 0).toLocaleString()}
          </p>
          {showTrend ? (
            <div className="mt-3">
              <Sparkline points={trend} valueLabel={label} />
              <p className="mt-1 text-xs font-medium text-bgray-500">
                Cumulative since {trend[0].label}
              </p>
            </div>
          ) : (
            <p className="mt-3 text-xs font-medium text-bgray-500">
              No change on record
            </p>
          )}
        </>
      )}
    </div>
  );
};

// The fill carries severity and the track is a lighter step of the same ramp,
// so the state reads across the whole bar. The percentage is always written
// out too - severity is never colour alone.
const Meter = ({ label, used, total, detail }) => {
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const fill =
    pct >= 90 ? "bg-error-300" : pct >= 70 ? "bg-warning-300" : "bg-success-300";
  const track =
    pct >= 90 ? "bg-error-50" : pct >= 70 ? "bg-warning-100" : "bg-success-50";

  return (
    <div className="mb-5 last:mb-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-bgray-600 dark:text-bgray-50">{label}</span>
        <span className="text-sm font-bold text-bgray-900 dark:text-bgray-50">
          {formatBytes(used)} / {formatBytes(total)}
          <span className="ml-2 text-bgray-500">{pct}%</span>
        </span>
      </div>
      <div className={`h-2 w-full overflow-hidden rounded-full ${track}`}>
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
      {detail && <p className="mt-1.5 text-xs text-bgray-500">{detail}</p>}
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-bgray-200 py-2.5 last:border-0 dark:border-darkblack-400">
    <span className="shrink-0 text-sm font-medium text-bgray-600 dark:text-bgray-50">
      {label}
    </span>
    <span className="truncate text-sm font-bold text-bgray-900 dark:text-bgray-50">
      {value}
    </span>
  </div>
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [totalDeviceData, settotalDeviceData] = useState(null);
  const [totalcustomerData, settotalcustomerData] = useState(null);
  const [totalasset, setasset] = useState(null);
  const [totaluser, setuser] = useState(null);
  const [systeminfomation, setsysteminfo] = useState(null);

  // The full rows are kept now, not just their length, because the sparklines
  // are built from each record's created_time.
  useEffect(() => {
    fetchDashboard()
      .then((res) => setDashboardData(res.data))
      .catch((err) => console.error(err));

    totalDevices()
      .then((res) => settotalDeviceData(res.data))
      .catch((err) => console.error(err));

    totalcustomer()
      .then((res) => settotalcustomerData(res.data))
      .catch((err) => console.error(err));

    asset()
      .then((res) => setasset(res.data))
      .catch((err) => console.error(err));

    users()
      .then((res) => setuser(res.data))
      .catch((err) => console.error(err));

    systeminfo()
      .then((res) => setsysteminfo(res.data))
      .catch((err) => console.error(err));
  }, []);

  const cards = [
    { label: "Total Dashboard", rows: dashboardData, icon: <RxDashboard /> },
    { label: "Total Devices", rows: totalDeviceData, icon: <MdOutlineDevices /> },
    { label: "Total Customer", rows: totalcustomerData, icon: <BsBuilding /> },
    { label: "Total Asset", rows: totalasset, icon: <MdOutlineInventory2 /> },
    { label: "Total Users", rows: totaluser, icon: <FiUsers /> },
  ];

  const cpu = systeminfomation?.cpu;
  const memory = systeminfomation?.memory;
  const disks = Array.isArray(systeminfomation?.disk) ? systeminfomation.disk : [];

  const cpuName = cpu
    ? [cpu.manufacturer, cpu.brand].filter(Boolean).join(" ").trim() || "-"
    : "-";
  const coreText = cpu
    ? cpu.physicalCores && cpu.physicalCores !== cpu.cores
      ? `${cpu.cores} logical / ${cpu.physicalCores} physical`
      : `${cpu.cores ?? "-"}`
    : "-";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        {cards.map((card) => (
          <StatCard key={card.label} {...card} loading={card.rows === null} />
        ))}
      </div>

      <div className="rounded-lg border border-bgray-200 bg-white dark:border-darkblack-400 dark:bg-darkblack-600 xl:max-w-[560px]">
        <div className="border-b border-bgray-200 px-5 py-4 dark:border-darkblack-400">
          <h3 className="text-lg font-bold text-bgray-900 dark:text-white">
            System Information
          </h3>
          <p className="text-sm text-bgray-600 dark:text-bgray-50">
            Live figures from the API server
          </p>
        </div>

        <div className="px-5 py-4">
          {systeminfomation ? (
            <>
              <div className="mb-5">
                <InfoRow label="Processor" value={cpuName} />
                <InfoRow label="Cores" value={coreText} />
                {cpu?.speed && <InfoRow label="Base speed" value={`${cpu.speed} GHz`} />}
              </div>

              {memory?.total ? (
                <Meter
                  label="Memory"
                  used={memory.used}
                  total={memory.total}
                  detail={`${formatBytes(memory.free)} free`}
                />
              ) : null}

              {disks.length > 0 ? (
                disks.map((d) => (
                  <Meter
                    key={d.mount || d.fs}
                    label={`Disk (${d.mount || d.fs})`}
                    used={d.used}
                    total={d.size}
                    detail={`${formatBytes(d.available)} available${d.type ? ` - ${d.type}` : ""}`}
                  />
                ))
              ) : (
                <p className="text-sm text-bgray-500">No disk information reported.</p>
              )}
            </>
          ) : (
            <Skeleton count={5} height={22} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
